import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Mail,
  CalendarClock,
  Lightbulb,
  Rocket,
  ShieldCheck,
  Download,
  CheckCircle2,
  Terminal,
  CircleDot,
  Zap,
  BookOpen,
  FileText,
  Briefcase,
  Search,
  ImageIcon,
  Code2,
  MessageSquare,
  BarChart3,
  Users,
  Package,
  Clock,
  Wrench,
  DollarSign,
  Handshake,
  Star,
  Layers,
  Cpu,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prompt-mastery-64/artifacts/3omzwe9h_1773800644378.png";
const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/daa6b42e-c1e9-4071-b7c9-e2090d1094c2/images/fe2a1f0ef8f17a0d6a548c7bb1739933153f84e00e82465ac4527757f07a77d6.png";

// URLs — update the placeholders when the courses go live
const URLS = {
  chatgpt: "https://justin-s-school217.teachable.com/p/7-day-chatgpt-quickstart",
  claude: "https://justin-s-school217.teachable.com/p/7-day-claude-quickstart-challenge",
  gemini: "https://justin-s-school217.teachable.com/l/pdp/7-day-gemini-quickstart-challenge",
  copilot: "https://justin-s-school217.teachable.com/p/copilotquickstart",
  executiveWorkflow: "https://justin-s-school217.teachable.com/p/ai-executive-workflow-from-data-to-decision-in-15-minutes",
  twentyDay: "#",
  mastery: "#",
  academyDomain: "https://jkacademy.academy",
  techDomain: "https://jkacademy.tech",
};

// ---------- Shared bits ----------
const Container = ({ className = "", children }) => (
  <div className={`max-w-7xl mx-auto px-6 sm:px-8 ${className}`}>{children}</div>
);

const Overline = ({ children }) => (
  <span className="font-mono text-[11px] sm:text-xs font-bold uppercase tracking-[0.28em] text-cyan-400">
    {children}
  </span>
);

const ext = (href) =>
  href && href !== "#"
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : { href: "#" };

// ---------- Header ----------
const Header = () => (
  <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
    <Container className="flex items-center justify-between h-16 sm:h-20">
      <a href="#top" data-testid={LANDING.navLogo} className="flex items-center gap-3">
        <img src={LOGO_URL} alt="JK Academy" className="h-9 w-9 sm:h-10 sm:w-10 object-contain" />
        <div className="leading-tight">
          <div className="font-display font-extrabold tracking-tight text-white text-base sm:text-lg">
            JK ACADEMY
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 hidden sm:block">
            Master AI · One Week at a Time
          </div>
        </div>
      </a>
      <nav className="hidden md:flex items-center gap-7 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
        <a href="#challenges" data-testid={LANDING.navChatgpt} className="hover:text-cyan-300 transition-colors">
          Challenges
        </a>
        <a href="#compare" data-testid={LANDING.navClaude} className="hover:text-cyan-300 transition-colors">
          Compare
        </a>
        <a href="#free" data-testid={LANDING.navFree} className="hover:text-cyan-300 transition-colors">
          Free Guide
        </a>
        <a href="/services" className="hover:text-cyan-300 transition-colors inline-flex items-center gap-1.5">
          Services
          <span className="px-1.5 py-0.5 bg-cyan-500 text-slate-950 text-[8px] font-bold tracking-[0.18em]">
            NEW
          </span>
        </a>
      </nav>
    </Container>
  </header>
);

const CYPHER_IMG = "/cypher.webp";

// ---------- Hero ----------
const Hero = () => (
  <section id="top" className="relative overflow-hidden">
    <div className="absolute inset-0 -z-10">
      <img
        src={HERO_BG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover opacity-35"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-[#030712]" />
      <div className="absolute inset-0 bg-grid opacity-60 animate-grid-pan" />
      <div className="absolute inset-0 bg-radial-glow" />
    </div>

    <Container className="pt-16 pb-24 sm:pt-24 sm:pb-32 lg:pt-28 lg:pb-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 mb-7">
            <CircleDot className="h-3.5 w-3.5 text-cyan-400 animate-pulse" />
            <Overline>The Home of JK Academy QuickStarts</Overline>
          </div>

          <h1
            data-testid={LANDING.heroHeadline}
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-white leading-[1.02]"
          >
            Master <span className="text-cyan-400 text-glow-cyan">AI</span>.
            <br className="hidden sm:block" />{" "}
            One{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Week</span>
              <span className="absolute -bottom-1 left-0 right-0 h-2 bg-cyan-500/30 -z-0" />
            </span>{" "}
            at a Time.
          </h1>

          <p className="mt-7 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed font-mono">
            Learn today&apos;s most powerful AI tools through practical{" "}
            <span className="text-cyan-300">7-Day QuickStart Challenges</span> designed for busy professionals. Master AI in just{" "}
            <span className="text-cyan-300">5 minutes a day</span>.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start gap-4 flex-wrap">
            <a
              {...ext(URLS.chatgpt)}
              data-testid={LANDING.heroCtaChatgpt}
              className="group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_42px_rgba(6,182,212,0.7)] transition-all"
            >
              <Rocket className="h-4 w-4" />
              Start ChatGPT QuickStart
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              {...ext(URLS.claude)}
              data-testid={LANDING.heroCtaClaude}
              className="group inline-flex items-center justify-center gap-3 bg-transparent hover:bg-cyan-500/10 text-cyan-300 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 border border-cyan-400/60 hover:border-cyan-300 transition-all"
            >
              <Cpu className="h-4 w-4" />
              Explore Claude QuickStart
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              {...ext(URLS.gemini)}
              data-testid={LANDING.heroCtaGemini}
              className="group inline-flex items-center justify-center gap-3 bg-transparent hover:bg-cyan-500/10 text-cyan-300 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 border border-cyan-400/60 hover:border-cyan-300 transition-all"
            >
              <Sparkles className="h-4 w-4" />
              Explore Gemini QuickStart
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "5", v: "Min/Day" },
              { k: "7", v: "Days" },
              { k: "$17", v: "Per Course" },
            ].map((s) => (
              <div key={s.v} className="border-l border-cyan-500/40 pl-4">
                <div className="font-display text-3xl sm:text-4xl font-black text-white">{s.k}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Cypher + floating holographic cards */}
        <div
          data-testid={LANDING.heroCypherStage}
          className="lg:col-span-5 relative hidden md:block"
          aria-hidden
        >
          <div className="relative aspect-square max-w-[520px] mx-auto lg:ml-auto animate-fade-up">
            {/* Ambient cyan glow behind Cypher */}
            <div className="absolute inset-[8%] rounded-full bg-cyan-500/20 blur-3xl -z-10 animate-pulse-glow" />
            <div className="absolute inset-[20%] rounded-full bg-blue-500/10 blur-2xl -z-10" />

            {/* Floating holographic course cards */}
            <FloatingCourseCard
              icon={Rocket}
              label="ChatGPT"
              sub="QuickStart"
              className="absolute top-[6%] left-[-4%] w-[36%] rotate-[-6deg]"
              delay="0s"
            />
            <FloatingCourseCard
              icon={Cpu}
              label="Claude"
              sub="QuickStart"
              className="absolute top-[38%] left-[-10%] w-[36%] rotate-[-3deg]"
              delay="0.6s"
            />
            <FloatingCourseCard
              icon={Sparkles}
              label="Gemini"
              sub="QuickStart"
              className="absolute top-[70%] left-[6%] w-[36%] rotate-[-8deg]"
              delay="1.2s"
            />

            {/* Cypher */}
            <img
              src={CYPHER_IMG}
              alt="Cypher — the JK Academy AI guide"
              data-testid={LANDING.heroCypherImg}
              className="relative z-10 w-full h-full object-contain drop-shadow-[0_0_60px_rgba(6,182,212,0.35)]"
              style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.6))" }}
            />

            {/* Bottom cyan reflection line */}
            <div className="absolute bottom-[4%] left-[15%] right-[15%] h-[2px] bg-gradient-to-r from-transparent via-cyan-400/80 to-transparent blur-sm" />
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// ---------- Floating course card (used inside Hero stage) ----------
const FloatingCourseCard = ({ icon: Icon, label, sub, className = "", delay = "0s" }) => (
  <div
    className={`z-20 backdrop-blur-md bg-slate-900/60 border border-cyan-400/40 px-4 py-3 shadow-[0_0_30px_rgba(6,182,212,0.35)] ${className}`}
    style={{ animation: `fade-up 1.2s cubic-bezier(0.22, 1, 0.36, 1) ${delay} both` }}
  >
    <div className="absolute inset-0 -z-10 bg-gradient-to-br from-cyan-500/10 to-transparent" />
    <div className="flex items-center gap-2">
      <div className="h-7 w-7 shrink-0 flex items-center justify-center border border-cyan-400/50 bg-cyan-500/10 text-cyan-300">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-white text-xs leading-tight truncate">
          {label}
        </div>
        <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-cyan-300 truncate">
          {sub}
        </div>
      </div>
    </div>
  </div>
);

// ---------- Featured Challenges ----------
const CHALLENGES = [
  {
    slug: "chatgpt",
    icon: Rocket,
    tag: "Bestseller",
    tagTone: "cyan",
    title: "7-Day ChatGPT QuickStart",
    bullets: ["Beginner Friendly", "5 Minutes Per Day", "Real World Prompts", "Productivity", "Creativity"],
    price: "$17",
    cta: "Start ChatGPT",
    url: URLS.chatgpt,
  },
  {
    slug: "claude",
    icon: Cpu,
    tag: "NEW",
    tagTone: "electric",
    title: "7-Day Claude QuickStart",
    bullets: ["Writing", "Research", "Long Documents", "Projects", "Business Workflows"],
    price: "$17",
    cta: "Start Claude",
    url: URLS.claude,
  },
  {
    slug: "gemini",
    icon: Sparkles,
    tag: "NEW",
    tagTone: "electric",
    title: "7-Day Gemini QuickStart",
    bullets: ["Beginner Friendly", "5 Minutes Per Day", "Google Workspace", "Research", "Productivity", "Better Prompting"],
    price: "$17",
    cta: "Start Gemini",
    url: URLS.gemini,
  },
  {
    slug: "copilot",
    icon: MessageSquare,
    tag: "NEW",
    tagTone: "electric",
    title: "7-Day Copilot QuickStart",
    bullets: ["Microsoft 365", "Word · Excel · Outlook", "Teams Workflows", "Business Productivity", "Beginner Friendly"],
    price: "$17",
    cta: "Start Copilot",
    url: URLS.copilot,
    internal: "/copilot",
  },
  {
    slug: "executive-workflow",
    icon: BarChart3,
    tag: "NEW",
    tagTone: "electric",
    title: "AI Executive Workflow",
    bullets: ["Data → Decision in 15 min", "Executive Reporting", "Strategic Insights", "Cross-Tool Playbooks", "Leadership Focused"],
    price: "$17",
    cta: "View Details",
    url: URLS.executiveWorkflow,
    internal: "/executive-workflow",
  },
];

const ChallengeCard = ({ c }) => {
  const Icon = c.icon;
  const tagCls =
    c.tagTone === "electric"
      ? "bg-blue-500 text-slate-950"
      : "bg-cyan-500 text-slate-950";
  const isInternal = !!c.internal;
  const CTAWrap = isInternal ? Link : "a";
  const ctaProps = isInternal ? { to: c.internal } : ext(c.url);
  return (
    <div
      data-testid={LANDING.challengeCard(c.slug)}
      className="group relative border border-white/10 bg-slate-900/50 backdrop-blur-md p-8 sm:p-10 hover:border-cyan-500/60 hover:bg-slate-900/70 transition-all duration-300 flex flex-col"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/0 to-transparent group-hover:via-cyan-500/70 transition-all duration-500" />
      <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400/50 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400/50 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400/50 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400/50 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="flex items-center justify-between mb-8">
        <div className="h-14 w-14 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 transition-colors">
          <Icon className="h-6 w-6" />
        </div>
        <span
          className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] font-bold ${tagCls}`}
        >
          {c.tag}
        </span>
      </div>

      <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight">
        {c.title}
      </h3>

      <ul className="mt-6 space-y-3 flex-1">
        {c.bullets.map((b) => (
          <li key={b} className="flex items-center gap-3 text-slate-300 text-sm">
            <CheckCircle2 className="h-4 w-4 text-cyan-400 shrink-0" />
            <span className="font-mono">{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 pt-6 border-t border-white/10 flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            One-time
          </div>
          <div className="font-display text-4xl font-black text-white">{c.price}</div>
        </div>
        <CTAWrap
          {...ctaProps}
          data-testid={LANDING.challengeCta(c.slug)}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-xs px-5 py-3 shadow-[0_0_24px_rgba(6,182,212,0.4)] hover:shadow-[0_0_36px_rgba(6,182,212,0.7)] transition-all"
        >
          {c.cta}
          <ArrowRight className="h-3.5 w-3.5" />
        </CTAWrap>
      </div>
    </div>
  );
};

const FeaturedChallenges = () => (
  <section id="challenges" className="relative py-24 sm:py-32">
    <Container>
      <div className="max-w-2xl mb-14">
        <Overline>// section_02 — courses</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Available AI QuickStart Courses
        </h2>
        <p className="mt-4 text-slate-400 font-mono text-sm sm:text-base leading-relaxed">
          Five focused courses. Same format. Pick the tool you&apos;ll use most — or complete them all.
        </p>
      </div>

      <div
        data-testid={LANDING.challengesGrid}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 sm:gap-8"
      >
        {CHALLENGES.map((c) => (
          <ChallengeCard key={c.slug} c={c} />
        ))}
      </div>
    </Container>
  </section>
);

// ---------- Which AI Fits You Best ----------
const COMPARISON = {
  chatgpt: {
    title: "ChatGPT",
    subtitle: "Everyday productivity powerhouse",
    icon: Rocket,
    items: [
      { icon: Zap, text: "Everyday productivity" },
      { icon: Lightbulb, text: "Brainstorming" },
      { icon: Sparkles, text: "Creativity" },
      { icon: ImageIcon, text: "Images" },
      { icon: Code2, text: "Coding" },
    ],
  },
  claude: {
    title: "Claude",
    subtitle: "Deep work & serious writing",
    icon: Cpu,
    items: [
      { icon: FileText, text: "Professional writing" },
      { icon: Search, text: "Research" },
      { icon: BookOpen, text: "Long documents" },
      { icon: BarChart3, text: "Deep analysis" },
      { icon: Briefcase, text: "Business planning" },
    ],
  },
  gemini: {
    title: "Gemini",
    subtitle: "Google Workspace supercharged",
    icon: Sparkles,
    items: [
      { icon: Briefcase, text: "Google Workspace" },
      { icon: Mail, text: "Gmail" },
      { icon: FileText, text: "Docs" },
      { icon: BarChart3, text: "Sheets" },
      { icon: CalendarClock, text: "Calendar" },
      { icon: Zap, text: "Everyday productivity" },
    ],
  },
  copilot: {
    title: "Copilot",
    subtitle: "Microsoft 365 at work",
    icon: MessageSquare,
    items: [
      { icon: FileText, text: "Word documents" },
      { icon: BarChart3, text: "Excel automation" },
      { icon: Mail, text: "Outlook email" },
      { icon: Users, text: "Teams meetings" },
      { icon: Briefcase, text: "Enterprise workflows" },
    ],
  },
  executive: {
    title: "AI Exec Workflow",
    subtitle: "Data → decision in 15 min",
    icon: BarChart3,
    items: [
      { icon: BarChart3, text: "Executive reporting" },
      { icon: Lightbulb, text: "Strategic insights" },
      { icon: Layers, text: "Cross-tool playbooks" },
      { icon: Briefcase, text: "Leadership workflows" },
      { icon: Handshake, text: "Stakeholder briefs" },
    ],
  },
};

const CompareColumn = ({ col, testId }) => {
  const Icon = col.icon;
  return (
    <div
      data-testid={testId}
      className="relative border border-white/10 bg-slate-900/40 backdrop-blur-sm p-8 sm:p-10 hover:border-cyan-500/40 transition-colors"
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="h-12 w-12 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-none">
            {col.title}
          </h3>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
            {col.subtitle}
          </p>
        </div>
      </div>
      <ul className="space-y-4">
        {col.items.map((it) => {
          const I = it.icon;
          return (
            <li
              key={it.text}
              className="flex items-center gap-3 text-slate-200 text-base border-b border-white/5 pb-3 last:border-0"
            >
              <I className="h-4 w-4 text-cyan-400 shrink-0" />
              <span>{it.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const AiComparison = () => (
  <section
    id="compare"
    data-testid={LANDING.comparisonSection}
    className="relative py-24 sm:py-32 border-t border-white/5"
  >
    <div className="absolute inset-0 -z-10 bg-radial-glow opacity-40" />
    <Container>
      <div className="max-w-2xl mb-14">
        <Overline>// section_03 — pick your path</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Which AI Fits You Best?
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 sm:gap-8">
        <CompareColumn col={COMPARISON.chatgpt} testId={LANDING.comparisonChatgpt} />
        <CompareColumn col={COMPARISON.claude} testId={LANDING.comparisonClaude} />
        <CompareColumn col={COMPARISON.gemini} testId={LANDING.comparisonGemini} />
        <CompareColumn col={COMPARISON.copilot} testId={LANDING.comparisonCopilot} />
        <CompareColumn col={COMPARISON.executive} testId={LANDING.comparisonExecutive} />
      </div>

      <div className="mt-12 text-center">
        <p className="font-display text-2xl sm:text-3xl font-bold text-white">
          Not sure where to begin?
        </p>
        <p className="mt-3 font-mono text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
          Choose the AI that fits your workflow — or complete all five to build a{" "}
          <span className="text-cyan-300">complete AI foundation</span>.
        </p>
      </div>

      {/* Cypher callout */}
      <div
        data-testid={LANDING.cypherCallout}
        className="mt-10 max-w-3xl mx-auto relative bg-slate-900/60 backdrop-blur-md border border-cyan-500/30 shadow-[0_0_40px_rgba(6,182,212,0.15)]"
      >
        <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
        <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
        <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
        <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

        <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6 p-6 sm:p-8">
          <div className="relative shrink-0">
            <div className="absolute inset-0 rounded-full bg-cyan-500/25 blur-2xl animate-pulse-glow" />
            <img
              src={CYPHER_IMG}
              alt="Cypher, the JK Academy AI guide"
              className="relative h-24 w-24 sm:h-28 sm:w-28 object-contain drop-shadow-[0_0_24px_rgba(6,182,212,0.5)]"
            />
          </div>
          <div className="text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] font-bold text-cyan-300">
                Cypher Recommends
              </span>
            </div>
            <p className="font-display text-base sm:text-lg text-slate-200 leading-relaxed">
              Choose <span className="text-cyan-300 font-bold">ChatGPT</span> for everyday AI,{" "}
              <span className="text-cyan-300 font-bold">Claude</span> for writing,{" "}
              <span className="text-cyan-300 font-bold">Gemini</span> for Google,{" "}
              <span className="text-cyan-300 font-bold">Copilot</span> for Microsoft 365, or the{" "}
              <span className="text-cyan-300 font-bold">Executive Workflow</span> for leadership.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// ---------- Free Resources (existing email capture — preserved) ----------
const FreeResources = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = `${API}/guide/download`;
    a.download = "10-ChatGPT-Prompts-That-Save-Time.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Please enter your name.";
    if (!email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "That email looks off.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        website_url: websiteUrl,
      });
      setSubmitted(true);
      setWebsiteUrl(""); // clear honeypot after success so autofill can't silently kill future submits
      toast.success("You're in. Your free guide is downloading.", {
        description: "Check your downloads folder for the PDF.",
      });
      triggerDownload();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      if (status === 429) {
        toast.error("Slow down — too many submissions.", {
          description: msg || "Please wait a few minutes and try again.",
        });
      } else {
        toast.error("Couldn't submit — please try again.", { description: msg || "Network error" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setName("");
    setEmail("");
    setWebsiteUrl(""); // clear honeypot too, so stale/autofill value cannot silently kill submissions
    setErrors({});
  };

  return (
    <section id="free" className="relative py-24 sm:py-32">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-2">
            <Overline>// section_04 — free ai starter resources</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              Free AI Starter <span className="text-cyan-400">Resources</span>
            </h2>
            <p className="mt-5 text-slate-400 font-mono text-sm leading-relaxed">
              Start today with our most popular free download. More free resources coming.
            </p>

            <div className="mt-8 border border-cyan-500/20 bg-slate-900/40 p-5">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 shrink-0 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                    Free · PDF
                  </span>
                  <h3 className="mt-1 font-display text-lg font-bold text-white leading-tight">
                    10 ChatGPT Prompts That Save Time
                  </h3>
                  <p className="mt-1.5 font-mono text-xs text-slate-400 leading-relaxed">
                    Copy-paste prompts for work, productivity, organization, and everyday life.
                  </p>
                </div>
              </div>
            </div>

            <ul className="mt-6 space-y-3">
              {["10 copy-paste prompts", "Beginner-friendly format", "No spam · ever"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-300 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-cyan-400 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="relative bg-slate-900/50 backdrop-blur-md border border-white/10 p-8 sm:p-12 shadow-[0_0_60px_rgba(6,182,212,0.15)]">
              <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
              <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
              <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
              <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

              {!submitted ? (
                <form
                  data-testid={LANDING.emailCaptureForm}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  noValidate
                >
                  {/* Honeypot — hidden from users, visible to bots */}
                  <div
                    aria-hidden="true"
                    className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden opacity-0 pointer-events-none"
                  >
                    <label htmlFor="website_url">Website</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-2">
                    <Terminal className="h-3.5 w-3.5" />
                    <span>/ download.access</span>
                  </div>

                  <div>
                    <label htmlFor="name" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                      Your name
                    </label>
                    <input
                      id="name"
                      data-testid={LANDING.inputName}
                      type="text"
                      autoComplete="name"
                      placeholder="Jordan Kim"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`w-full bg-slate-950 border ${
                        errors.name ? "border-red-500/60" : "border-white/10"
                      } text-white placeholder:text-slate-600 h-12 px-4 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors`}
                    />
                    {errors.name && (
                      <p className="mt-2 font-mono text-xs text-red-400">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                      Email address
                    </label>
                    <input
                      id="email"
                      data-testid={LANDING.inputEmail}
                      type="email"
                      autoComplete="email"
                      placeholder="jordan@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full bg-slate-950 border ${
                        errors.email ? "border-red-500/60" : "border-white/10"
                      } text-white placeholder:text-slate-600 h-12 px-4 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors`}
                    />
                    {errors.email && (
                      <p className="mt-2 font-mono text-xs text-red-400">{errors.email}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    data-testid={LANDING.submitGuideButton}
                    disabled={submitting}
                    className="w-full group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_42px_rgba(6,182,212,0.7)] transition-all"
                  >
                    <Download className="h-4 w-4" />
                    {submitting ? "Preparing…" : "Download Free Guide"}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>

                  <p className="text-[11px] font-mono text-slate-500 text-center">
                    By submitting you agree to receive the guide + occasional emails from JK Academy.
                  </p>
                </form>
              ) : (
                <div className="text-center space-y-6">
                  <div className="mx-auto h-16 w-16 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 animate-pulse-glow">
                    <CheckCircle2 className="h-8 w-8 text-cyan-400" />
                  </div>
                  <div>
                    <Overline>// transmission complete</Overline>
                    <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-white">
                      You&apos;re in, {name.split(" ")[0] || "friend"}.
                    </h3>
                    <p className="mt-3 text-slate-400 font-mono text-sm leading-relaxed">
                      Your PDF has started downloading. If it didn&apos;t, tap the button below.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      data-testid={LANDING.downloadAgainButton}
                      onClick={triggerDownload}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-xs px-6 py-3.5 shadow-[0_0_24px_rgba(6,182,212,0.4)] transition-all"
                    >
                      <Download className="h-4 w-4" />
                      Download again
                    </button>
                    <button
                      data-testid={LANDING.resetFormButton}
                      onClick={reset}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent hover:bg-slate-800 text-white font-semibold font-mono uppercase tracking-[0.18em] text-xs px-6 py-3.5 border border-white/20 hover:border-white/40 transition-all"
                    >
                      Send to another email
                    </button>
                  </div>

                  <div className="mt-4 relative border border-cyan-500/30 bg-gradient-to-br from-slate-950/80 to-cyan-950/20 p-5 sm:p-6 text-left overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 shrink-0 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-400">
                        <Rocket className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                            Recommended next
                          </span>
                          <span className="px-1.5 py-0.5 bg-cyan-500 text-slate-950 font-mono text-[9px] uppercase tracking-[0.18em] font-bold">
                            Most popular
                          </span>
                        </div>
                        <h4 className="font-display text-base sm:text-lg font-bold text-white leading-snug">
                          Ready in 7 days — not 7 months.
                        </h4>
                        <p className="mt-1.5 font-mono text-xs text-slate-400 leading-relaxed">
                          Take the <span className="text-cyan-300">7-Day ChatGPT QuickStart</span> and turn these 10 prompts into a daily habit.
                        </p>
                        <a
                          {...ext(URLS.chatgpt)}
                          data-testid={LANDING.successUpsellButton}
                          className="group mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] font-bold text-cyan-300 hover:text-cyan-200 border-b border-cyan-400/40 hover:border-cyan-300 pb-1 transition-colors"
                        >
                          Start the QuickStart
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

// ---------- Why JK Academy ----------
const WHY_ITEMS = [
  { icon: Clock, text: "Short lessons that fit busy schedules" },
  { icon: BookOpen, text: "Beginner-friendly instruction" },
  { icon: Briefcase, text: "Practical workplace examples" },
  { icon: DollarSign, text: "Affordable pricing" },
  { icon: Wrench, text: "Learn by doing" },
];

const WhyJk = () => (
  <section className="relative py-24 sm:py-32 border-t border-white/5">
    <div className="absolute inset-0 -z-10 bg-radial-glow opacity-30" />
    <Container>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
        <div className="md:col-span-8 max-w-2xl">
          <Overline>// section_05 — why jk academy</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Why Learn with JK Academy?
          </h2>
          <p className="mt-4 text-slate-400 font-mono text-sm sm:text-base leading-relaxed">
            Built for people who want real AI skills — without the fluff or the 40-hour course. Cypher will walk you through every step.
          </p>
        </div>
        <div
          data-testid={LANDING.whyCypher}
          className="md:col-span-4 relative flex justify-center md:justify-end"
          aria-hidden
        >
          <div className="relative w-40 sm:w-48 md:w-52 shrink-0">
            <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-2xl animate-pulse-glow" />
            <img
              src={CYPHER_IMG}
              alt="Cypher waving hello"
              className="relative w-full h-auto object-contain drop-shadow-[0_0_36px_rgba(6,182,212,0.4)]"
            />
            <div className="absolute -top-2 -right-4 sm:right-0 border border-cyan-400/50 bg-slate-950/80 backdrop-blur-sm px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">
              👋 Hi, I&apos;m Cypher
            </div>
          </div>
        </div>
      </div>

      <ul
        data-testid={LANDING.whyList}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
      >
        {WHY_ITEMS.map((w, i) => {
          const Icon = w.icon;
          return (
            <li
              key={w.text}
              data-testid={LANDING.whyItem(i)}
              className="group relative flex items-start gap-4 border border-white/10 bg-slate-900/40 backdrop-blur-sm p-6 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all"
              style={{ animation: `fade-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${0.1 + i * 0.08}s both` }}
            >
              <div className="relative h-10 w-10 shrink-0 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
                <Icon className="h-5 w-5" />
                <CheckCircle2 className="absolute -top-2 -right-2 h-4 w-4 text-cyan-300 bg-slate-950 rounded-full transition-transform group-hover:scale-125" />
              </div>
              <div className="pt-1">
                <p className="font-display text-base sm:text-lg font-semibold text-white leading-snug">
                  {w.text}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </Container>
  </section>
);

// ---------- AI QuickStart Library ----------
const LIBRARY_AVAILABLE = [
  { slug: "chatgpt", icon: Rocket, title: "ChatGPT QuickStart", status: "Available", url: URLS.chatgpt },
  { slug: "claude", icon: Cpu, title: "Claude QuickStart", status: "Available", url: URLS.claude },
  { slug: "gemini", icon: Sparkles, title: "Gemini QuickStart", status: "NEW", url: URLS.gemini },
  { slug: "copilot", icon: MessageSquare, title: "Copilot QuickStart", status: "NEW", url: "/copilot", subtitle: "$17 · 7 days" },
  { slug: "executive-workflow", icon: BarChart3, title: "AI Executive Workflow", status: "NEW", url: "/executive-workflow", subtitle: "View Course Details" },
];

const LIBRARY_UPCOMING = [
  { slug: "perplexity", icon: Search, title: "Perplexity QuickStart" },
  { slug: "notebooklm", icon: BookOpen, title: "NotebookLM QuickStart" },
  { slug: "canva", icon: ImageIcon, title: "Canva AI QuickStart" },
];

const StatusPill = ({ status }) => {
  if (status === "Available") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 px-2.5 py-1">
        <CheckCircle2 className="h-3 w-3" />
        Available
      </span>
    );
  }
  if (status === "NEW") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-slate-950 bg-cyan-500 px-2.5 py-1">
        <Star className="h-3 w-3" />
        New
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 border border-white/15 bg-slate-900/40 px-2.5 py-1">
      <CircleDot className="h-3 w-3 animate-pulse" />
      Coming Soon
    </span>
  );
};

const LibraryCard = ({ item, upcoming = false }) => {
  const Icon = item.icon;
  const isInternal = !upcoming && item.url && item.url.startsWith("/") && !item.url.startsWith("//");
  let Wrapper, wrapperProps;
  if (upcoming) {
    Wrapper = "div";
    wrapperProps = {};
  } else if (isInternal) {
    Wrapper = Link;
    wrapperProps = { to: item.url };
  } else {
    Wrapper = "a";
    wrapperProps = ext(item.url);
  }
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={LANDING.libraryCard(item.slug)}
      className={`group relative border ${
        upcoming ? "border-dashed border-white/15 bg-slate-900/30" : "border-white/10 bg-slate-900/50"
      } backdrop-blur-sm p-6 sm:p-7 transition-all ${
        upcoming ? "hover:border-cyan-500/40" : "hover:border-cyan-500/60 hover:bg-slate-900/70 cursor-pointer"
      } flex flex-col`}
    >
      <div className="flex items-start justify-between mb-5">
        <div
          className={`h-11 w-11 flex items-center justify-center border ${
            upcoming
              ? "border-cyan-500/25 bg-cyan-500/5 text-cyan-300/80"
              : "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <StatusPill status={upcoming ? "Soon" : item.status} />
      </div>
      <h3 className="font-display text-lg sm:text-xl font-bold text-white leading-tight">
        {item.title}
      </h3>
      {!upcoming && (
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {item.subtitle || "$17 · 5 min/day"}
          </span>
          <ArrowRight className="h-4 w-4 text-cyan-400 transition-transform group-hover:translate-x-1" />
        </div>
      )}
    </Wrapper>
  );
};

const QuickStartLibrary = () => (
  <section
    id="library"
    data-testid={LANDING.librarySection}
    className="relative py-24 sm:py-32 border-t border-white/5"
  >
    <Container>
      <div className="max-w-2xl mb-14">
        <Overline>// section_06 — the library</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          The JK Academy <span className="text-cyan-400">AI QuickStart Library</span>
        </h2>
        <p className="mt-4 text-slate-400 font-mono text-sm sm:text-base leading-relaxed">
          Every course we&apos;ve shipped — plus what&apos;s next on the roadmap.
        </p>
      </div>

      {/* Available */}
      <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
        / available now
      </div>
      <div
        data-testid={LANDING.libraryAvailableGrid}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-14"
      >
        {LIBRARY_AVAILABLE.map((item) => (
          <LibraryCard key={item.slug} item={item} />
        ))}
      </div>

      {/* Upcoming */}
      <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
        / coming soon
      </div>
      <div
        data-testid={LANDING.libraryUpcomingGrid}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6"
      >
        {LIBRARY_UPCOMING.map((item) => (
          <LibraryCard key={item.slug} item={item} upcoming />
        ))}
      </div>
    </Container>
  </section>
);

// ---------- Future Bundle Teaser ----------
const BundleTeaser = () => (
  <section
    data-testid={LANDING.bundleTeaser}
    className="relative py-24 sm:py-32 border-t border-white/5 overflow-hidden"
  >
    <div className="absolute inset-0 -z-10 bg-grid opacity-30" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-cyan-500/[0.05] to-transparent" />
    <Container>
      <div className="max-w-3xl mx-auto text-center">
        <Overline>// section_07 — future collection</Overline>
        <div className="mt-5 inline-flex items-center gap-2 border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 mb-6">
          <Package className="h-3.5 w-3.5 text-cyan-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-cyan-300">
            Bundle Preview
          </span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          AI QuickStart <span className="text-cyan-400">Collection</span>
        </h2>
        <p className="mt-5 font-mono text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl mx-auto">
          Soon you&apos;ll be able to bundle every JK Academy 7-Day Challenge together at a discounted price.
        </p>

        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          {["ChatGPT", "Claude", "Gemini", "Perplexity", "Copilot", "+ More"].map((tag) => (
            <span
              key={tag}
              className="font-mono text-[11px] uppercase tracking-[0.2em] border border-white/10 bg-slate-900/40 text-slate-300 px-3 py-1.5"
            >
              {tag}
            </span>
          ))}
        </div>

        <button
          disabled
          data-testid={LANDING.bundleCta}
          className="mt-10 inline-flex items-center gap-3 bg-transparent text-slate-400 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 border border-white/20 cursor-not-allowed"
        >
          <Layers className="h-4 w-4" />
          Coming Soon
        </button>
      </div>
    </Container>
  </section>
);

// ---------- Instructor ----------
const Instructor = () => (
  <section
    data-testid={LANDING.instructorSection}
    className="relative py-24 sm:py-32 border-t border-white/5"
  >
    <Container>
      <div className="max-w-2xl mb-12">
        <Overline>// section_08 — instructor</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Meet Your Instructor
        </h2>
      </div>

      <div className="relative max-w-3xl border border-white/10 bg-slate-900/40 backdrop-blur-sm p-8 sm:p-12">
        <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400/50" />
        <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400/50" />
        <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400/50" />
        <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400/50" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8">
          <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 font-display font-black text-3xl sm:text-4xl">
            JM
          </div>
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
              Justin May
            </h3>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
              Founder · JK Academy
            </p>
            <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
              Helping professionals master today&apos;s leading AI tools through practical, beginner-friendly QuickStart courses.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// ---------- Footer ----------
const FooterCol = ({ title, links }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-400 mb-4">
      {title}
    </div>
    <ul className="space-y-2.5">
      {links.map((l) => (
        <li key={l.label}>
          <a
            {...ext(l.href)}
            data-testid={l.testId}
            className="font-display text-sm text-slate-300 hover:text-cyan-300 transition-colors"
          >
            {l.label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <footer className="border-t border-white/10 bg-slate-950/60">
    <Container className="py-16">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-10 gap-y-12">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="JK Academy" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white text-base">JK ACADEMY</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                Master AI · One Week
              </div>
            </div>
          </div>
          <p className="font-mono text-xs text-slate-400 leading-relaxed max-w-xs">
            Practical, affordable 7-Day AI QuickStart Challenges for busy professionals.
          </p>
        </div>

        <FooterCol
          title="QuickStart Courses"
          links={[
            { label: "ChatGPT", href: URLS.chatgpt, testId: LANDING.footerChatgpt },
            { label: "Claude", href: URLS.claude, testId: LANDING.footerClaude },
            { label: "Gemini", href: URLS.gemini, testId: LANDING.footerGemini },
            { label: "Copilot", href: "/copilot", testId: LANDING.footerCopilot },
            { label: "Executive Workflow", href: "/executive-workflow", testId: "footer-executive-workflow" },
          ]}
        />
        <FooterCol
          title="Coming Soon"
          links={[
            { label: "Perplexity", href: "#library", testId: LANDING.footerPerplexity },
            { label: "NotebookLM", href: "#library", testId: LANDING.footerNotebooklm },
            { label: "Canva AI", href: "#library", testId: "footer-canva" },
          ]}
        />
        <FooterCol
          title="Free Learning"
          links={[
            { label: "Free Prompt Guide", href: "#free", testId: LANDING.footerFreeGuide },
            { label: "20-Day ChatGPT Challenge", href: URLS.twentyDay, testId: LANDING.footerTwentyDay },
          ]}
        />
        <FooterCol
          title="Premium Learning"
          links={[{ label: "AI Prompting Mastery", href: URLS.mastery, testId: LANDING.footerMastery }]}
        />
      </div>

      <div className="mt-16 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <a
          {...ext(URLS.techDomain)}
          className="group border border-cyan-500/30 bg-slate-900/40 p-5 hover:border-cyan-400 transition-colors"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-400 mb-1">
            Primary
          </div>
          <div className="font-display text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
            JKACADEMY.TECH
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 mt-1">
            Premium QuickStarts
          </div>
        </a>
        <a
          {...ext(URLS.academyDomain)}
          className="group border border-white/10 bg-slate-900/40 p-5 hover:border-cyan-400/50 transition-colors"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-400 mb-1">
            Sister site
          </div>
          <div className="font-display text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
            JKACADEMY.ACADEMY
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 mt-1">
            Free Challenges & Advanced Courses
          </div>
        </a>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
          © {new Date().getFullYear()} JK Academy · Built for humans + AI
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">
          Master AI. One Week at a Time.
        </p>
      </div>
    </Container>
  </footer>
);

// ---------- Page ----------
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#030712] text-slate-100">
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          className: "font-mono",
          style: {
            background: "#0F172A",
            border: "1px solid rgba(6,182,212,0.4)",
            color: "#F8FAFC",
            borderRadius: 0,
          },
        }}
      />
      <Header />
      <main>
        <Hero />
        <FeaturedChallenges />
        <AiComparison />
        <FreeResources />
        <WhyJk />
        <QuickStartLibrary />
        <BundleTeaser />
        <Instructor />
      </main>
      <Footer />
    </div>
  );
}
